import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, apiErr } from '@/lib/api';
import { ShieldCheck, Copy, Check, KeyRound } from 'lucide-react';

export default function TwoFactorSetup({ open, onOpenChange, onEnabled }) {
  const [step, setStep] = useState('password'); // password | scan | verify | recovery
  const [password, setPassword] = useState('');
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri, qr_data_url}
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setStep('password'); setPassword(''); setSetup(null);
      setCode(''); setCodes([]); setError(''); setCopied(false);
    }
  }, [open]);

  const startSetup = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/2fa/setup', { password });
      setSetup(data); setStep('scan');
    } catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  };

  const enable = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/2fa/enable', { code });
      setCodes(data.recovery_codes || []);
      setStep('recovery');
      toast.success('2FA activado');
      onEnabled?.();
    } catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(setup.secret); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const copyCodes = async () => {
    try { await navigator.clipboard.writeText(codes.join('\n')); toast.success('Códigos copiados'); } catch {}
  };

  const finish = () => { onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="twofa-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary"/> Activar 2FA</DialogTitle>
          <DialogDescription>
            Compatible con Microsoft Authenticator, Authy y Google Authenticator.
          </DialogDescription>
        </DialogHeader>

        {step === 'password' && (
          <div className="space-y-4">
            <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Confirma tu contraseña actual</Label>
            <Input type="password" data-testid="twofa-password" className="h-12" value={password} onChange={(e) => setPassword(e.target.value)}/>
            {error && <p className="text-sm text-destructive" data-testid="twofa-error">{error}</p>}
            <Button data-testid="twofa-start" onClick={startSetup} disabled={loading || !password} className="w-full h-11 rounded-full">Continuar</Button>
          </div>
        )}

        {step === 'scan' && setup && (
          <div className="space-y-4">
            <p className="text-sm">Escanea el código QR con tu app de autenticación:</p>
            <div className="flex justify-center">
              <img data-testid="twofa-qr" src={setup.qr_data_url} alt="QR 2FA" className="w-56 h-56 rounded-md border border-border"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">O ingresa esta clave manual</Label>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-border p-2.5 font-mono-num text-sm break-all" data-testid="twofa-secret">
                <span className="flex-1">{setup.secret}</span>
                <button onClick={copySecret} className="p-1.5 rounded hover:bg-secondary" data-testid="twofa-copy-secret">
                  {copied ? <Check className="h-4 w-4 text-primary"/> : <Copy className="h-4 w-4"/>}
                </button>
              </div>
            </div>
            <Button data-testid="twofa-continue" onClick={() => setStep('verify')} className="w-full h-11 rounded-full">Ya escaneé el código</Button>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-4">
            <p className="text-sm">Ingresa el código de 6 dígitos que muestra tu app de autenticación:</p>
            <Input
              inputMode="numeric" pattern="[0-9]*" maxLength={6}
              data-testid="twofa-code"
              className="h-14 text-center font-mono-num text-2xl tracking-[0.5em]"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
            />
            {error && <p className="text-sm text-destructive" data-testid="twofa-error">{error}</p>}
            <Button data-testid="twofa-enable" onClick={enable} disabled={loading || code.length !== 6} className="w-full h-11 rounded-full">
              Confirmar activación
            </Button>
          </div>
        )}

        {step === 'recovery' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <KeyRound className="h-5 w-5"/>
              <p className="font-semibold">Guarda tus 10 códigos de recuperación</p>
            </div>
            <p className="text-xs text-muted-foreground">Cada código sirve UNA sola vez. Úsalos si pierdes acceso a tu app de autenticación.</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 bg-secondary/40 font-mono-num text-sm" data-testid="twofa-recovery-codes">
              {codes.map((c, i) => (<div key={i} data-testid={`recovery-${i}`}>{c}</div>))}
            </div>
            <Button variant="outline" onClick={copyCodes} className="w-full h-11 rounded-full" data-testid="twofa-copy-codes"><Copy className="h-4 w-4 mr-2"/> Copiar todos</Button>
            <Button data-testid="twofa-finish" onClick={finish} className="w-full h-11 rounded-full">Listo</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
